/**
 * FreeAgent HTTP Client
 * 
 * Handles OAuth2 token management, base URL, headers, retries, and error shaping.
 * No business logic - pure HTTP layer.
 */

import { freeagentConfig } from '../config/freeagentConfig';

export interface FreeAgentError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

/**
 * FreeAgent returns validation failures (422) as `{ errors: { error: { message } } }` or
 * `{ errors: [{ message }] }`. Collects every message so the user sees FreeAgent's reason.
 */
export function freeAgentValidationMessage(details: any): string | null {
  const messages: string[] = [];
  const walk = (value: any) => {
    if (!value) return;
    if (typeof value === 'string') { messages.push(value); return; }
    if (Array.isArray(value)) { value.forEach(walk); return; }
    if (typeof value === 'object') {
      if (typeof value.message === 'string') { messages.push(value.message); return; }
      Object.values(value).forEach(walk);
    }
  };
  walk(details?.errors);
  const unique = Array.from(new Set(messages.map((message) => message.trim()).filter(Boolean)));
  return unique.length ? `FreeAgent: ${unique.join('; ')}` : null;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  retries?: number;
}

class FreeAgentHttpClient {
  private baseUrl: string;
  private userAgent: string;
  private clientId: string;
  private clientSecret: string;
  private serverlessBase: string;

  constructor() {
    this.baseUrl = freeagentConfig.baseUrl;
    this.userAgent = freeagentConfig.userAgent;
    this.clientId = freeagentConfig.clientId;
    // Client secret is no longer used in browser - token exchange happens server-side
    this.clientSecret = '';
    const envBase = process.env.REACT_APP_FA_SERVERLESS_BASE || '';
    this.serverlessBase = envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;

    if (!this.clientId || !this.userAgent) {
      throw new Error('FreeAgent client ID and user agent are required');
    }
  }

  /**
   * Get stored access token from localStorage
   */
  private getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;
    const token = localStorage.getItem(freeagentConfig.tokenStoreKey);
    const expiry = localStorage.getItem(freeagentConfig.tokenExpiryStoreKey);
    
    if (!token || !expiry) return null;
    
    // Check if token is expired
    const expiryTime = parseInt(expiry, 10);
    if (Date.now() >= expiryTime) {
      return null; // Token expired
    }
    
    return token;
  }

  /**
   * Store access token in localStorage
   */
  private storeToken(tokenResponse: TokenResponse): void {
    if (typeof window === 'undefined') return;
    
    const expiryTime = Date.now() + (tokenResponse.expires_in * 1000);
    
    localStorage.setItem(freeagentConfig.tokenStoreKey, tokenResponse.access_token);
    localStorage.setItem(freeagentConfig.refreshTokenStoreKey, tokenResponse.refresh_token);
    localStorage.setItem(freeagentConfig.tokenExpiryStoreKey, expiryTime.toString());
  }

  /**
   * Get stored refresh token
   */
  private getStoredRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(freeagentConfig.refreshTokenStoreKey);
  }

  /**
   * Clear stored tokens
   */
  private clearTokens(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(freeagentConfig.tokenStoreKey);
    localStorage.removeItem(freeagentConfig.refreshTokenStoreKey);
    localStorage.removeItem(freeagentConfig.tokenExpiryStoreKey);
  }

  /**
   * Exchange authorization code for access token (OAuth2)
   * Uses serverless function to keep client secret secure
   */
  async exchangeCodeForToken(code: string, redirectUri: string): Promise<TokenResponse> {
    // Use serverless function for token exchange (keeps client secret server-side)
    const tokenEndpoint = this.serverlessBase
      ? `${this.serverlessBase}/api/freeagent/token`
      : '/api/freeagent/token';
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirectUri,
      }),
    });

    if (!response.ok) {
      const error = await this.shapeError(response);
      throw error;
    }

    const tokenResponse: TokenResponse = await response.json();
    this.storeToken(tokenResponse);
    return tokenResponse;
  }

  /**
   * Refresh access token using refresh token
   * Uses serverless function to keep client secret secure
   */
  async refreshAccessToken(): Promise<TokenResponse> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Use serverless function for token refresh (keeps client secret server-side)
    const refreshEndpoint = this.serverlessBase
      ? `${this.serverlessBase}/api/freeagent/refresh`
      : '/api/freeagent/refresh';
    const response = await fetch(refreshEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken,
      }),
    });

    if (!response.ok) {
      // If refresh fails, clear tokens
      this.clearTokens();
      const error = await this.shapeError(response);
      throw error;
    }

    const tokenResponse: TokenResponse = await response.json();
    this.storeToken(tokenResponse);
    return tokenResponse;
  }

  /**
   * Get valid access token (from cache or refresh)
   */
  async getAccessToken(): Promise<string> {
    let token = this.getStoredToken();
    
    if (!token) {
      // Try to refresh
      const tokenResponse = await this.refreshAccessToken();
      token = tokenResponse.access_token;
    }
    
    if (!token) {
      throw new Error('No access token available. Please authenticate first.');
    }
    
    return token;
  }

  /**
   * Shape error response into consistent format
   */
  private async shapeError(response: Response): Promise<FreeAgentError> {
    let details: any = {};
    const raw = await response.text().catch(() => '');
    if (raw) {
      try {
        details = JSON.parse(raw);
      } catch {
        details = { message: raw };
      }
    }

    return {
      message: details.error_description || details.message || freeAgentValidationMessage(details) || `HTTP ${response.status}`,
      status: response.status,
      code: details.error || details.code,
      details,
    };
  }

  /**
   * Make HTTP request with automatic retries and token management
   */
  async request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      retries = 3,
    } = options;

    // Get access token
    const token = await this.getAccessToken();

    // Build request headers
    const requestHeaders: Record<string, string> = {
      'User-Agent': this.userAgent,
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    };

    // Build request options
    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
    };

    if (body) {
      requestOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
    }

    // Retry logic
    let lastError: FreeAgentError | null = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(`${this.baseUrl}${path}`, requestOptions);

        // Handle 401 Unauthorized - try to refresh token once
        if (response.status === 401 && attempt === 0) {
          try {
            await this.refreshAccessToken();
            // Retry with new token
            const newToken = await this.getAccessToken();
            requestHeaders['Authorization'] = `Bearer ${newToken}`;
            requestOptions.headers = requestHeaders;
            continue;
          } catch (refreshError) {
            // Refresh failed, throw original error
            throw await this.shapeError(response);
          }
        }

        if (!response.ok) {
          throw await this.shapeError(response);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        } else {
          return {} as T;
        }
      } catch (error: any) {
        lastError = error as FreeAgentError;
        
        // Don't retry on client errors (4xx) except 401
        if (error.status && error.status >= 400 && error.status < 500 && error.status !== 401) {
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === retries) {
          throw error;
        }

        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }

    throw lastError || new Error('Request failed after retries');
  }
}

// Export singleton instance
export const freeAgentHttpClient = new FreeAgentHttpClient();


